<script module lang="ts">
  export const shareModal = new ModalState('share');
</script>

<script
  lang="ts"
>
  import { PUBLIC_SITE_DOMAIN } from '$app/env/public';
  import ContentPaste from '~icons/mdi/content-paste';
  import Share from '~icons/mdi/share';
  import {
    copyShareImage,
    copyShareText,
    getShareText,
  } from '~/gameOver/getShareText';
  import ActionButton from '~/components/ActionButton.svelte';
  import OGPreview from './OGPreview.svelte';
  import Modal from '~/components/Modal.svelte';
  import Close from '~icons/mdi/close';
  import Image from '~icons/mdi/image';
  import Note from '~icons/mdi/note';
  import Link from '~icons/mdi/link';
  import {
    generateThumbMutation,
    uploadThumbMutation,
  } from './mutations.svelte';
  import Spinner from '~/components/Spinner.svelte';
  import { fade } from 'svelte/transition';
  import DailyThumb from './DailyThumb.svelte';
  import ClassicThumb from './ClassicThumb.svelte';
  import { dev } from '$app/env';
  import { getClassicRankQuery } from '~/lib/queries.svelte';
  import { ModalState } from '~/lib/nav.svelte';
  import { getValueSerializer } from '~/store/valueSerializer';
  import ShortUniqueId from 'short-unique-id';
  import { untrack } from 'svelte';
  import { isClassic, isDaily, type Match } from '~/types';
  import { getGame } from '~/store/GameContext.svelte';
  import { PersistedState } from 'runed';
  import { playerName } from '~/auth/utils.svelte';

  const uid = new ShortUniqueId({ length: 8 });
  type Props = {
    words: Match[];
  };

  const props: Props = $props();

  type ShareMethod = 'text' | 'link' | 'img';

  const game = $derived(getGame().current);
  const text = $derived(getShareText(game, props.words));
  const uploadThumb = uploadThumbMutation();
  const generateThumb = generateThumbMutation();
  const classicRank = getClassicRankQuery('week');

  let imgRef = $state<HTMLImageElement | undefined>(undefined);
  let domRef = $state<HTMLDivElement | undefined>(undefined);
  let clickedButton = $state<ShareMethod>('img');

  let selectedShareMethod = new PersistedState<ShareMethod>(
    'shareMethod',
    'link',
    {
      serializer: getValueSerializer<ShareMethod>(),
    },
  );
  const shareMethods = [
    { icon: Link, type: 'link', text: 'Share link with thumbnail' },
    ...typeof ClipboardItem !== 'undefined'
      ? [{ icon: Image, type: 'image', text: 'Share direct image' }]
      : [],
    { icon: Note, type: 'text', text: 'Share text' }
  ];

  $effect(() => {
    if (
      domRef &&
      generateThumb.isIdle &&
      selectedShareMethod.current !== 'text'
    ) {
      handleGenerate();
    }
  });

  $effect(() => {
    if (imgRef && generateThumb.isSuccess) {
      untrack(() => {
        (imgRef!).src = generateThumb.data.png;
      });
    }
  });

  const handleGenerate = async () => {
    // if (!$fonts.data) {
    //   await $fonts.mutateAsync(domRef!);
    // }
    untrack(async () => {
      if (domRef) {
        await generateThumb.mutateAsync({ domRef });
      }
    });
  };

  const handleShare = () => {
    switch (selectedShareMethod.current) {
      case 'link':
        const id = uid.rnd();
        if (generateThumb.data?.blob && uploadThumb.isIdle) {
          uploadThumb.mutate({ blob: generateThumb.data.blob, id });
        }
        handleShareLink(id);
        break;
      case 'img':
        handleShareImg();
        break;
      case 'text':
        handleShareText();
        break;
    }
    shareModal.pop();
  };

  const handleShareLink = async (imagePath: string) => {
    const link = `${PUBLIC_SITE_DOMAIN}/s/${imagePath}`;
    if (navigator.share) {
      await navigator.share({
        title: 'Gram Jam',
        url: link,
      });
    } else {
      copyShareText(link);
    }
    clickedButton = 'link';
  };

  const handleShareImg = async () => {
    if (generateThumb.isSuccess && generateThumb.data.blob) {
      const shareImg = generateThumb.data.blob;
      await copyShareImage(shareImg);
      clickedButton = 'img';
    }
  };

  const handleShareText = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error(err);
      copyShareText(text);
    }
    clickedButton = 'text';
  };

  const handleClose = () => {
    shareModal.pop();
  };
</script>

<Modal index={3} open={shareModal.open} onClose={handleClose}>
  {#snippet title()}
    Share your score
  {/snippet}
  {#snippet content()}
    <div class="image-container">
      {#if dev}
        <ActionButton
          onClick={() => {
            handleGenerate();
          }}
        >
          regenerate
        </ActionButton>
      {/if}
      {#if generateThumb.isPending}
        <div transition:fade class="spinner">
          generating
          <Spinner />
        </div>
      {/if}
      {#if selectedShareMethod.current === 'text'}
        <p class="share-text">
          <span class="copy-button">
            <ActionButton
              class={clickedButton === 'text' ? 'shared' : ''}
              onClick={handleShareText}
            >
              <ContentPaste />
            </ActionButton>
          </span>
          {text.split('\n').slice(0, -1).join('\n')}
          {'\n'}
          <a href={PUBLIC_SITE_DOMAIN}>{PUBLIC_SITE_DOMAIN}</a>
        </p>
      {:else}
        <OGPreview bind:domRef bind:imgRef>
          {#if isDaily(game)}
            {#await game.getAll() then words}
              <DailyThumb words={words} />
            {/await}
          {:else if isClassic(game)}
            <ClassicThumb
              name={playerName.current}
              position={classicRank.data}
              bestWord={props.words[0]}
            />
          {/if}
        </OGPreview>
        <div class="image-desc">
          <h3>Beat my score at Gram Jam</h3>
          <p>
            Dive into this puzzly tile swapping word game, combining letters to
            score big words!
          </p>
          <a href="https://gramjam.app">gramjam.app</a>
        </div>
      {/if}
    </div>
  {/snippet}
  {#snippet controls()}
    <div class="controls">
      {#each shareMethods as method}
        <label class="radio-button">
          <input
            value={method.type}
            type="radio"
            name="share-method"
            bind:group={selectedShareMethod.current}
          />
          <span class="checkmark"></span>
          <method.icon size="1.5em" />
          {method.text}
        </label>
      {/each}
      <div class="button-group">
        <ActionButton onClick={handleClose}>
          <Close />
          Close
        </ActionButton>
        <ActionButton disabled={generateThumb.isPending} onClick={handleShare}>
          <Share />
          Share
        </ActionButton>
      </div>
    </div>
  {/snippet}
</Modal>

<style lang="scss">
  @use '~/theme/theme';
  :global(.action.shared) {
    @include theme.pulse-animation(theme.$tile-blue-dark);
  }
  .image-container {
    width: calc(100% - 2em);
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: start;
    text-align: left;
    border: 2px solid black;
    border-radius: 1em;
    overflow: hidden;
    & * {
      margin: 0;
    }
    .spinner {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: rgba(0, 0, 0, 0.4);
    }
    :global(html.dark-mode) & {
      border-color: white;
    }
  }
  .share-text {
    box-sizing: border-box;
    white-space: pre-line;
    padding: 1em;
    font-size: 1.25rem;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    & :global(.action) {
      position: absolute;
      top: 0.5em;
      right: 0.5em;
      padding: 0.5em;
      justify-content: center;
      font-size: 1.25em;
      &:active {
        @include theme.pulse-animation(theme.$tile-blue-dark);
      }
    }
  }
  .spinner {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .image-desc {
    border-top: 2px solid black;
    padding: 1em;
    padding-top: 0.5em;
    a {
      color: theme.$tile-blue-dark;
    }
    :global(html.dark-mode) & {
      border-color: white;
      a {
        color: theme.$tile-blue-light;
      }
    }
  }
  .radio-button {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1em;
    padding: 0.5em 0;
    position: relative;
    padding-left: 32px;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    cursor: pointer;
    /* Hide the browser's default checkbox */
    & input {
      position: absolute;
      opacity: 0;
      height: 0;
      width: 0;
    }
    /* Create a custom checkbox */
    & .checkmark {
      z-index: 2;
      transition: all 0.2s ease;
      position: absolute;
      top: 10px;
      left: 0;
      height: 16px;
      width: 16px;
      border-radius: 50%;
      background-color: rgba(127, 127, 127, 0);
      outline: rgba(0, 0, 0, 0);
      border: 2px solid black;
      :global(html.dark-mode) & {
        border-color: white;
      }
    }
    & .checkmark::after {
      z-index: 1;
      content: '';
      transition: all 0.2s ease;
      position: absolute;
      width: 8px;
      height: 8px;
      top: 2px;
      left: 2px;
      border-radius: 50%;
    }
    &:hover .checkmark {
      background-color: rgba(127, 127, 127, 0.4);
      outline: 4px solid rgba(127, 127, 127, 127.4);
    }
    & input:checked ~ .checkmark {
      height: 16px;
      width: 16px;
      display: block;
    }
    & input:checked ~ .checkmark::after {
      background-color: black;
      :global(html.dark-mode) & {
        background-color: white;
      }
    }
  }
  .controls {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 0.25em;
  }
  .button-group {
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 0.5em;
    padding-top: 1em;
    :global(.action svg) {
      width: 20px;
      height: 20px;
    }
  }
</style>
